import React, { useMemo, useState } from 'react';

function AcademyMockup({ language = 'ro', onBack }) {
  const isRo = language === 'ro';
  const [selectedModuleId, setSelectedModuleId] = useState('m1');
  const [detailOpen, setDetailOpen] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [lessonFilter, setLessonFilter] = useState('all');
  const [sectionPage, setSectionPage] = useState({});

  const c = useMemo(
    () => ({
      back: isRo ? 'Înapoi' : 'Back',
      title: isRo ? 'Academy UI Mockup' : 'Academy UI Mockup',
      subtitle: isRo
        ? 'Grid mode pentru module + pagină dedicată de lecții pe modul. Navigation rail-ul din stânga rămâne.'
        : 'Grid mode for modules + dedicated lesson page per module. Left navigation rail stays.',
      viewAll: isRo ? 'Vezi toate modulele' : 'View all modules',
      lessons: isRo ? 'lecții' : 'lessons',
      progress: isRo ? 'Progres' : 'Progress',
      inCourse: isRo ? 'În curs' : 'In progress',
      unlocked: isRo ? 'Deblocat' : 'Unlocked',
      locked: isRo ? 'Locked' : 'Locked',
      detailsTitle: isRo ? 'Modul selectat' : 'Selected module',
      lessonListTitle: isRo ? 'Lecții în modul' : 'Lessons in module',
      continueCta: isRo ? 'Continuă lecția' : 'Continue lesson',
      lockedHint: isRo ? 'Disponibil în plan superior' : 'Available in higher plan',
      lessonLocked: isRo ? 'Necesită abonament' : 'Requires subscription',
      section: isRo ? 'Secțiunea' : 'Section',
      lessonRange: isRo ? 'Lecții' : 'Lessons',
      primaryAction: isRo ? 'Acțiune principală' : 'Primary action',
      continueWhereLeft: isRo ? 'Continuă de unde ai rămas' : 'Continue where you left off',
      moduleSummary: isRo ? 'Rezumat modul' : 'Module summary',
      moduleProgress: isRo ? 'Progres modul' : 'Module progress',
      iqToGain: isRo ? 'IQ points disponibile' : 'IQ points available',
      lessonsDone: isRo ? 'Lecții finalizate' : 'Completed lessons',
      lessonCount: isRo ? 'lecții' : 'lessons',
      nextBest: isRo ? 'Următoarea lecție recomandată' : 'Recommended next lesson',
      filterAll: isRo ? 'Toate' : 'All',
      filterInProgress: isRo ? 'În curs' : 'In progress',
      filterPending: isRo ? 'Nefinalizate' : 'Not finished',
      filterLocked: isRo ? 'Locked' : 'Locked',
      prevPage: isRo ? 'Anterior' : 'Prev',
      nextPage: isRo ? 'Următor' : 'Next',
      page: isRo ? 'Pagina' : 'Page',
      noLessonsForFilter: isRo ? 'Nu există lecții pentru filtrul selectat.' : 'No lessons for this filter.',
      academy: 'Academy',
      puzzles: isRo ? 'Puzzle-uri' : 'Puzzles',
      openings: isRo ? 'Deschideri' : 'Openings',
      quizHub: 'Quiz Hub',
      mindHub: 'Mind Hub',
      community: 'Community'
    }),
    [isRo]
  );

  const modules = useMemo(
    () => [
      {
        id: 'm1',
        title: isRo ? 'Modul 1: Fundamente' : 'Module 1: Fundamentals',
        lessons: 16,
        progress: 78,
        status: 'in_course',
        iqLeft: 120,
        completedLessons: 11,
        nextLessonId: 'm1-l13',
        image: '♟️',
        summary: isRo
          ? 'Baze solide: piese, centru, reguli esențiale și primele decizii strategice.'
          : 'Solid basics: pieces, center, core rules, and first strategic decisions.',
        lessonItems: [
          { id: 'm1-l1', title: isRo ? 'Povestea șahului' : 'Chess story', status: 'done' },
          { id: 'm1-l2', title: isRo ? 'Codul de onoare' : 'Code of honor', status: 'done' },
          { id: 'm1-l3', title: isRo ? 'Tabla de șah: cum o citești' : 'Read the chessboard', status: 'done' },
          { id: 'm1-l4', title: isRo ? 'Structura tablei: linii și coloane' : 'Board structure: files and ranks', status: 'done' },
          { id: 'm1-l5', title: isRo ? 'Centrul tablei' : 'The center', status: 'done' },
          { id: 'm1-l6', title: isRo ? 'Pionul: primul soldat' : 'Pawn: first soldier', status: 'done' },
          { id: 'm1-l7', title: isRo ? 'Turnul: gardianul liniilor' : 'Rook: line guardian', status: 'done' },
          { id: 'm1-l8', title: isRo ? 'Nebunul: diagonale active' : 'Bishop: active diagonals', status: 'done' },
          { id: 'm1-l9', title: isRo ? 'Calul: singurul care sare' : 'Knight: the jumper', status: 'done' },
          { id: 'm1-l10', title: isRo ? 'Dama: regina atacului' : 'Queen: attack ruler', status: 'done' },
          { id: 'm1-l11', title: isRo ? 'Regele: piesa de protejat' : 'King: piece to protect', status: 'done' },
          { id: 'm1-l12', title: isRo ? 'Valoarea pieselor 1-3-5-9' : 'Piece values 1-3-5-9', status: 'available' },
          { id: 'm1-l13', title: isRo ? 'SCA: Șahuri, Capturi, Amenințări' : 'CCT: Checks, Captures, Threats', status: 'in_progress' },
          { id: 'm1-recap', title: isRo ? 'Recapitulare modul' : 'Module recap', status: 'available' },
          { id: 'm1-exam', title: isRo ? 'Examen final standard' : 'Final standard exam', status: 'available' },
          { id: 'm1-captain', title: isRo ? 'Examen de căpitan' : 'Captain exam', status: 'locked' }
        ]
      },
      {
        id: 'm2',
        title: isRo ? 'Modul 2: Reguli speciale' : 'Module 2: Special rules',
        lessons: 22,
        progress: 24,
        status: 'unlocked',
        iqLeft: 180,
        completedLessons: 5,
        nextLessonId: 'm2-l6',
        image: '🏰',
        summary: isRo
          ? 'Rocadă, en passant, promovare și situații de validare legală pe tablă.'
          : 'Castling, en passant, promotion, and legal validation scenarios on board.',
        lessonItems: [
          { id: 'm2-l1', title: isRo ? 'Rocada: scutul regelui' : "Castling: king's shield", status: 'available' },
          { id: 'm2-l2', title: isRo ? 'En passant' : 'En passant', status: 'available' },
          { id: 'm2-l3', title: isRo ? 'Promovarea pionului' : 'Pawn promotion', status: 'available' },
          { id: 'm2-l4', title: isRo ? 'Reguli de validare' : 'Move legality rules', status: 'available' },
          { id: 'm2-l5', title: isRo ? 'Rocadă: cazuri ilegale' : 'Castling illegal cases', status: 'done' },
          { id: 'm2-l6', title: isRo ? 'SCA în reguli speciale' : 'CCT in special rules', status: 'in_progress' },
          { id: 'm2-l7', title: isRo ? 'Exerciții mixte #1' : 'Mixed drills #1', status: 'available' },
          { id: 'm2-l8', title: isRo ? 'Exerciții mixte #2' : 'Mixed drills #2', status: 'available' },
          { id: 'm2-l9', title: isRo ? 'Exerciții mixte #3' : 'Mixed drills #3', status: 'available' },
          { id: 'm2-l10', title: isRo ? 'Quiz rapid reguli' : 'Quick rules quiz', status: 'available' },
          { id: 'm2-l11', title: isRo ? 'Puzzle legalitate #1' : 'Legality puzzle #1', status: 'locked' },
          { id: 'm2-l12', title: isRo ? 'Puzzle legalitate #2' : 'Legality puzzle #2', status: 'locked' },
          { id: 'm2-l13', title: isRo ? 'Puzzle legalitate #3' : 'Legality puzzle #3', status: 'locked' },
          { id: 'm2-l14', title: isRo ? 'Mini examen reguli' : 'Rules mini exam', status: 'locked' },
          { id: 'm2-l15', title: isRo ? 'Module project #1' : 'Module project #1', status: 'locked' },
          { id: 'm2-l16', title: isRo ? 'Module project #2' : 'Module project #2', status: 'locked' },
          { id: 'm2-l17', title: isRo ? 'Challenge ladder #1' : 'Challenge ladder #1', status: 'locked' },
          { id: 'm2-l18', title: isRo ? 'Challenge ladder #2' : 'Challenge ladder #2', status: 'locked' },
          { id: 'm2-l19', title: isRo ? 'Challenge ladder #3' : 'Challenge ladder #3', status: 'locked' },
          { id: 'm2-l20', title: isRo ? 'Review final module' : 'Final module review', status: 'locked' },
          { id: 'm2-l21', title: isRo ? 'Examen standard' : 'Standard exam', status: 'locked' },
          { id: 'm2-l22', title: isRo ? 'Examen căpitan' : 'Captain exam', status: 'locked' }
        ]
      },
      {
        id: 'm3',
        title: isRo ? 'Modul 3: Tactică practică' : 'Module 3: Practical tactics',
        lessons: 18,
        progress: 0,
        status: 'unlocked',
        iqLeft: 240,
        completedLessons: 0,
        nextLessonId: 'm3-l1',
        image: '🎯',
        summary: isRo
          ? 'Atac, apărare, piese neprotejate și conversia avantajului fără risc inutil.'
          : 'Attack, defense, hanging pieces, and converting advantage without unnecessary risk.',
        lessonItems: [
          { id: 'm3-l1', title: isRo ? 'Atac și apărare' : 'Attack and defense', status: 'available' },
          { id: 'm3-l2', title: isRo ? 'Piese neprotejate' : 'Hanging pieces', status: 'available' },
          { id: 'm3-l3', title: isRo ? 'Conversie avantaj' : 'Advantage conversion', status: 'available' }
        ]
      },
      {
        id: 'm4',
        title: isRo ? 'Modul 4: Clubul de mat' : 'Module 4: Mate club',
        lessons: 14,
        progress: 0,
        status: 'locked',
        iqLeft: 260,
        completedLessons: 0,
        nextLessonId: 'm4-l1',
        image: '👑',
        summary: isRo
          ? 'Pattern-uri de mat, finalizare tehnică și execuție calmă sub presiune.'
          : 'Mate patterns, technical finishing, and calm execution under pressure.',
        lessonItems: [
          { id: 'm4-l1', title: isRo ? 'Mat cu turnul' : 'Rook mate', status: 'locked' },
          { id: 'm4-l2', title: isRo ? 'Mat cu dama' : 'Queen mate', status: 'locked' }
        ]
      }
    ],
    [isRo]
  );

  const selectedModule = modules.find((m) => m.id === selectedModuleId) || modules[0];

  const groupedLessons = useMemo(() => {
    const size = 10;
    const chunks = [];
    for (let i = 0; i < selectedModule.lessonItems.length; i += size) {
      const items = selectedModule.lessonItems.slice(i, i + size);
      const start = i + 1;
      const end = i + items.length;
      chunks.push({
        key: `${selectedModule.id}-${start}-${end}`,
        title: `${c.section} ${chunks.length + 1}`,
        range: `${c.lessonRange} ${start}-${end}`,
        items
      });
    }
    return chunks;
  }, [selectedModule, c.section, c.lessonRange]);

  const getStatusLabel = (status) => {
    if (status === 'in_course') return c.inCourse;
    if (status === 'unlocked') return c.unlocked;
    return c.locked;
  };

  return (
    <main className="academy-gridmock-page">
      <div className="academy-gridmock-head">
        <button className="academy-gridmock-back" onClick={onBack} type="button">
          ← {c.back}
        </button>
        <div>
          <h1>{c.title}</h1>
          <p>{c.subtitle}</p>
        </div>
      </div>

      <section className="academy-gridmock-shell">
        <aside className="academy-gridmock-rail">
          <button className="item active">{c.academy}</button>
          <button className="item">{c.puzzles}</button>
          <button className="item">{c.openings}</button>
          <button className="item">{c.quizHub}</button>
          <button className="item">{c.mindHub}</button>
          <button className="item">{c.community}</button>
        </aside>

        <section className="academy-gridmock-main">
          {!detailOpen && (
            <>
              <div className="academy-gridmock-toolbar">
                <h2>{isRo ? 'Toate modulele' : 'All modules'}</h2>
              </div>
              <div className="academy-gridmock-cards">
                {modules.map((m) => (
                  <article
                    key={m.id}
                    className={`academy-gridmock-card ${m.status === 'locked' ? 'locked' : ''}`}
                    onClick={() => {
                      setSelectedModuleId(m.id);
                      setDetailOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setSelectedModuleId(m.id);
                        setDetailOpen(true);
                      }
                    }}
                  >
                    <div className="card-status">{getStatusLabel(m.status)}</div>
                    <div className="card-hero">{m.image}</div>
                    <h3>{m.title}</h3>
                    <p>{m.summary}</p>
                    <div className="card-meta">
                      <span>{m.lessons} {c.lessons}</span>
                      <span>{c.progress}: {m.progress}%</span>
                    </div>
                    <div className="card-progress"><span style={{ width: `${m.progress}%` }} /></div>
                  </article>
                ))}
              </div>
            </>
          )}

          {detailOpen && (
            <section className="academy-gridmock-detail">
              <div className="academy-gridmock-toolbar">
                <h2>{c.detailsTitle}</h2>
                <button className="ghost" type="button" onClick={() => setDetailOpen(false)}>
                  {c.viewAll}
                </button>
              </div>

              <article className={`academy-gridmock-detail-card ${selectedModule.status === 'locked' ? 'locked' : ''}`}>
                <div className="detail-head">
                  <div className="detail-icon">{selectedModule.image}</div>
                  <div>
                    <h3>{selectedModule.title}</h3>
                    <p>{selectedModule.summary}</p>
                  </div>
                </div>

                <div className="detail-grid">
                  <div>
                    <div className="detail-label">{c.lessonListTitle}</div>
                    <div className="lesson-filters">
                      {[
                        { id: 'all', label: c.filterAll },
                        { id: 'in_progress', label: c.filterInProgress },
                        { id: 'pending', label: c.filterPending },
                        { id: 'locked', label: c.filterLocked }
                      ].map((f) => (
                        <button
                          key={f.id}
                          type="button"
                          className={`lesson-filter-btn ${lessonFilter === f.id ? 'active' : ''}`}
                          onClick={() => {
                            setLessonFilter(f.id);
                            setSectionPage({});
                          }}
                        >
                          {f.label}
                        </button>
                      ))}
                    </div>
                    <div className="detail-sections">
                      {groupedLessons.map((section, idx) => {
                        const isOpen = openSections[section.key] ?? idx === 0;
                        const filteredItems = section.items.filter((item) => {
                          if (lessonFilter === 'all') return true;
                          if (lessonFilter === 'in_progress') return item.status === 'in_progress';
                          if (lessonFilter === 'locked') return item.status === 'locked';
                          if (lessonFilter === 'pending') return item.status !== 'done';
                          return true;
                        });
                        const pageSize = 5;
                        const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
                        const currentPage = Math.min(sectionPage[section.key] || 1, totalPages);
                        const pageStart = (currentPage - 1) * pageSize;
                        const pagedItems = filteredItems.slice(pageStart, pageStart + pageSize);
                        return (
                          <section key={section.key} className="lesson-section">
                            <button
                              type="button"
                              className="section-toggle"
                              onClick={() =>
                                setOpenSections((prev) => ({ ...prev, [section.key]: !isOpen }))
                              }
                            >
                              <div>
                                <strong>{section.title}</strong>
                                <span>{section.range}</span>
                              </div>
                              <span aria-hidden="true">{isOpen ? '−' : '+'}</span>
                            </button>
                            {isOpen && (
                              <div className="detail-lessons">
                                {pagedItems.length === 0 && (
                                  <div className="lesson-empty">{c.noLessonsForFilter}</div>
                                )}
                                {pagedItems.map((l) => (
                                  <div key={l.id} className={`lesson-item ${l.status}`}>
                                    <span>{l.title}</span>
                                    <span>
                                      {l.status === 'done'
                                        ? 'Done'
                                        : l.status === 'in_progress'
                                        ? c.inCourse
                                        : l.status === 'locked'
                                        ? c.locked
                                        : c.unlocked}
                                    </span>
                                  </div>
                                ))}
                                {filteredItems.length > pageSize && (
                                  <div className="lesson-pagination">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSectionPage((prev) => ({
                                          ...prev,
                                          [section.key]: Math.max(1, currentPage - 1)
                                        }))
                                      }
                                      disabled={currentPage <= 1}
                                    >
                                      {c.prevPage}
                                    </button>
                                    <span>
                                      {c.page} {currentPage}/{totalPages}
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setSectionPage((prev) => ({
                                          ...prev,
                                          [section.key]: Math.min(totalPages, currentPage + 1)
                                        }))
                                      }
                                      disabled={currentPage >= totalPages}
                                    >
                                      {c.nextPage}
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </section>
                        );
                      })}
                    </div>
                  </div>

                  <div className="detail-focus">
                    <div className="detail-label">{c.primaryAction}</div>
                    <button
                      type="button"
                      className="focus-cta"
                      disabled={selectedModule.status === 'locked'}
                    >
                      {selectedModule.status === 'locked' ? c.lessonLocked : c.continueWhereLeft}
                    </button>
                    {selectedModule.status === 'locked' && (
                      <p className="locked-note">{c.lockedHint}</p>
                    )}
                    {selectedModule.status !== 'locked' && (
                      <div className="focus-widget">
                        <div className="focus-widget-title">{c.moduleSummary}</div>
                        <div className="focus-progress">
                          <span>{c.moduleProgress}</span>
                          <strong>{selectedModule.progress}%</strong>
                        </div>
                        <div className="focus-track">
                          <span style={{ width: `${selectedModule.progress}%` }} />
                        </div>
                        <div className="focus-metrics">
                          <div>
                            <span>{c.lessonsDone}</span>
                            <strong>
                              {selectedModule.completedLessons}/{selectedModule.lessonItems.length}
                            </strong>
                          </div>
                          <div>
                            <span>{c.iqToGain}</span>
                            <strong>{selectedModule.iqLeft}</strong>
                          </div>
                        </div>
                        <div className="focus-next">
                          <span>{c.nextBest}</span>
                          <strong>{selectedModule.lessonItems.find((x) => x.id === selectedModule.nextLessonId)?.title || '—'}</strong>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            </section>
          )}
        </section>
      </section>
    </main>
  );
}

export default AcademyMockup;
